import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Pause, 
  Play, 
  Square, 
  Lock,
  Unlock,
  Volume2,
  MapPin,
  MessageCircle,
  Trash2,
  Pencil,
  Navigation,
  Frown,
  Meh,
  Smile,
  Zap,
  Flame,
  Footprints,
  Trophy,
  Maximize2,
  X,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { SAFE_AREA } from "@/utils/safeArea";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Keyboard } from "@capacitor/keyboard";
import { Geolocation, Position } from "@capacitor/geolocation";
import { TextToSpeech } from "@capacitor-community/text-to-speech";
import { getTTSVoiceIndex } from "@/lib/ttsVoice";
import { setStraivNowPlaying, prepareAudioForRun, prepareBackgroundKeepAlive, resumeMusicAfterRunStart } from "@/lib/straivNowPlaying";
import { toast } from "sonner";
import { Analytics } from "@/lib/analytics";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { PlatformMap } from "@/components/PlatformMap";
import { ShareIcon } from "@/components/icons/ShareIcon";
import { useWorkouts } from "@/hooks/useWorkouts";
import { ShareWorkoutModal } from "@/components/ShareWorkoutModal";
import type { ShareableWorkout } from "@/components/ShareWorkoutCard";
import { RunLiveActivity } from "@/lib/runLiveActivity";
import type { GpsActivityKind } from "@/lib/gpsCardioWorkout";
import { exerciseNameForGpsActivity, workoutTypeForGpsActivity } from "@/lib/gpsCardioWorkout";

// BackgroundGeolocation for iOS - enables GPS when phone is locked
const BG_GEO_NAMES = ["BackgroundGeolocation", "CapacitorCommunityBackgroundGeolocation"];
let _bgGeo: any = null;
const getBackgroundGeolocation = () => {
  if (_bgGeo) return _bgGeo;
  for (const name of BG_GEO_NAMES) {
    try {
      const p = registerPlugin(name) as any;
      if (p && typeof (p as any).addWatcher === "function") {
        _bgGeo = p;
        return _bgGeo;
      }
    } catch (_) {}
  }
  return null;
};

// Haversine formula to calculate distance between two GPS points (in meters)
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

// Format seconds to HH:MM:SS (rounded for display)
const formatTime = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.round(totalSeconds % 60);
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Format pace (min/km) - always whole seconds, no decimals (e.g. 0:37 not 0:37.5)
const formatPace = (paceSeconds: number): string => {
  if (!paceSeconds || !isFinite(paceSeconds) || paceSeconds <= 0) return "--:--";
  const minutes = Math.floor(paceSeconds / 60);
  const seconds = Math.round(paceSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Map disabled temporarily due to react-leaflet bundling issues on iOS
// const RunningMap = lazy(() => import("@/components/running/RunningMap"));

interface GpsPoint {
  lat: number;
  lng: number;
  timestamp: number;
  accuracy?: number;
}

export const RunningActive = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { profile } = useProfile();
  const { createWorkout, addExerciseToWorkout, deleteWorkout } = useWorkouts();
  
  // Run state
  const [isRunning, setIsRunning] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [pauseCount, setPauseCount] = useState(0);
  
  // Dialog states
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [showRunSummary, setShowRunSummary] = useState(false);
  const [showAnalyzePrompt, setShowAnalyzePrompt] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showExpandedMap, setShowExpandedMap] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showBatteryPrompt, setShowBatteryPrompt] = useState(false);
  const [isSavingRun, setIsSavingRun] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [workoutToShare, setWorkoutToShare] = useState<ShareableWorkout | null>(null);
  
  // Map re-center state - use separate mapCenter to prevent constant refresh
  const [mapCenter, setMapCenter] = useState<[number, number] | null>(null);
  const [isMapCentered, setIsMapCentered] = useState(true); // Track if map is centered on user
  
  // Track if stop was confirmed (to prevent "run resumed" being said)
  const stopConfirmedRef = useRef(false);
  const [workoutSummary, setWorkoutSummary] = useState<any>(null);
  const [runNotes, setRunNotes] = useState("");
  const [runTitle, setRunTitle] = useState("");
  const [feelingEmoji, setFeelingEmoji] = useState<string>("");
  const [kmSplits, setKmSplits] = useState<{ km: number; time: string; pace: string }[]>([]);
  
  // GPS signal strength
  const [gpsSignal, setGpsSignal] = useState<'searching' | 'weak' | 'medium' | 'strong'>('searching');
  
  // Track distance for splits
  const lastSplitKmRef = useRef<number>(0);
  const splitTimesRef = useRef<{ km: number; elapsedSeconds: number }[]>([]);
  const elapsedSecondsRef = useRef<number>(0); // Ref for accurate timing in callbacks
  
  // GPS data - routeSegments: each segment is a continuous run (no movement during pause)
  const [currentPosition, setCurrentPosition] = useState<[number, number] | null>(null);
  const [routeSegments, setRouteSegments] = useState<GpsPoint[][]>([[]]);
  const [totalDistance, setTotalDistance] = useState(0); // in meters
  
  // Timer
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(Date.now());
  const pausedTimeRef = useRef<number>(0);
  
  // GPS restart key - increment when app becomes active to restart watch (fixes "location unavailable" after background)
  const [gpsRestartKey, setGpsRestartKey] = useState(0);
  
  // GPS watch - watchId for standard Geolocation, bgWatcherId for BackgroundGeolocation
  const watchIdRef = useRef<string | null>(null);
  const bgWatcherIdRef = useRef<string | null>(null);
  const lastPositionRef = useRef<GpsPoint | null>(null);
  
  // Audio announcements
  const lastAnnouncementKmRef = useRef<number>(0);
  const lastDistanceIntervalRef = useRef<number>(0);
  const audioIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasAnnouncedStartRef = useRef<boolean>(false);
  const announceStatsRef = useRef<() => void>(() => {});
  const lastManualAnnounceTimeRef = useRef<number>(0);
  const nowPlayingActiveRef = useRef(false);
  
  // Settings from RunningStart - load synchronously to prevent audio before settings load
  const [settings, setSettings] = useState<{
    activityType: string;
    audioStats: string;
    audioVoice?: 'female' | 'male';
    gpsActivity?: GpsActivityKind;
  }>(() => {
    // Load from localStorage synchronously during initialization
    try {
      const savedSettings = localStorage.getItem('runningSettings');
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings) as Record<string, unknown>;
        const g = parsed.gpsActivity;
        const gpsActivity: GpsActivityKind =
          g === 'walk' || g === 'hike' ? g : 'run';
        return { ...parsed, gpsActivity } as {
          activityType: string;
          audioStats: string;
          audioVoice?: 'female' | 'male';
          gpsActivity?: GpsActivityKind;
        };
      }
    } catch (e) {
      console.warn('Failed to load running settings:', e);
    }
    return { activityType: 'outdoor', audioStats: '5min', audioVoice: 'female', gpsActivity: 'run' };
  });

  const gpsKind: GpsActivityKind =
    settings.gpsActivity === 'walk' || settings.gpsActivity === 'hike'
      ? settings.gpsActivity
      : 'run';

  const gpsExerciseName = useMemo(() => exerciseNameForGpsActivity(gpsKind), [gpsKind]);
  const gpsWorkoutType = useMemo(() => workoutTypeForGpsActivity(gpsKind), [gpsKind]);

  const defaultTitleWithDate = useCallback(
    (dateStr: string) => {
      if (gpsKind === 'walk') return t('running.gpsActivity.titleWalkDate', { date: dateStr });
      if (gpsKind === 'hike') return t('running.gpsActivity.titleHikeDate', { date: dateStr });
      return t('running.gpsActivity.titleRunDate', { date: dateStr });
    },
    [gpsKind, t]
  );

  const defaultTitleWithKm = useCallback(
    (km: number) => {
      const d = km.toFixed(2);
      if (gpsKind === 'walk') return t('running.gpsActivity.titleWalkKm', { distance: d });
      if (gpsKind === 'hike') return t('running.gpsActivity.titleHikeKm', { distance: d });
      return t('running.gpsActivity.titleRunKm', { distance: d });
    },
    [gpsKind, t]
  );

  const workoutDescriptionLine = useCallback(
    (km: number, timeLabel: string) => {
      if (gpsKind === 'walk')
        return t('running.gpsActivity.descWalk', { distance: km, duration: timeLabel });
      if (gpsKind === 'hike')
        return t('running.gpsActivity.descHike', { distance: km, duration: timeLabel });
      return t('running.gpsActivity.descRun', { distance: km, duration: timeLabel });
    },
    [gpsKind, t]
  );
  
  // Run starts immediately when screen loads (no countdown)
  const runActive = true;
  
  // Restart GPS watch when app returns from background (fixes "location unavailable")
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const listener = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) setGpsRestartKey(k => k + 1);
    });
    return () => { listener.then(l => l.remove()); };
  }, []);

  // Android: Prompt to disable battery optimization for reliable background tracking
  // (OPPO, Xiaomi, Huawei etc. aggressively suspend apps; disabling helps GPS + TTS continue)
  useEffect(() => {
    if (!runActive || Capacitor.getPlatform() !== 'android') return;
    const check = async () => {
      try {
        const dismissed = localStorage.getItem('straiv_battery_prompt_dismissed');
        if (dismissed === 'true') return;
        const { BatteryOptimization } = await import('@capawesome-team/capacitor-android-battery-optimization');
        const { enabled } = await BatteryOptimization.isBatteryOptimizationEnabled();
        if (enabled) setShowBatteryPrompt(true);
      } catch (_) {}
    };
    void check();
  }, [runActive]);
  
  // Background persistence - save session periodically
  // Using refs to avoid stale closure issues
  const sessionDataRef = useRef<any>(null);
  
  // Flattened route for backward compat (storage, workout save) - memoized to prevent map flicker when typing
  const routePoints = useMemo(() => routeSegments.flat(), [routeSegments]);
  
  // Update session data ref whenever state changes
  useEffect(() => {
    sessionDataRef.current = {
      isRunning,
      isPaused,
      elapsedSeconds,
      totalDistance,
      routePoints,
      routeSegments,
      pauseCount,
      startTime: startTimeRef.current,
      pausedTime: pausedTimeRef.current,
      lastSplitKm: lastSplitKmRef.current,
      splitTimes: splitTimesRef.current,
      settings,
      timestamp: Date.now()
    };
  }, [isRunning, isPaused, elapsedSeconds, totalDistance, routeSegments, pauseCount, settings]);
  
  // Save session to localStorage periodically (only when run active, not during countdown)
  useEffect(() => {
    if (!runActive) return;
    
    const saveSession = () => {
      if (sessionDataRef.current) {
        sessionDataRef.current.timestamp = Date.now();
        try {
          localStorage.setItem('activeRunningSession', JSON.stringify(sessionDataRef.current));
        } catch (e) {
          console.warn('Failed to save running session:', e);
        }
      }
    };
    
    // Save immediately on start
    saveSession();
    
    // Then save every 5 seconds
    const saveInterval = setInterval(saveSession, 5000);
    return () => clearInterval(saveInterval);
  }, [runActive]);

  // Lock Screen + Dynamic Island Live Activity (iOS 16.2+, no-op elsewhere).
  // Starts as soon as we have an active run, pushes updates whenever stats
  // change (the wrapper throttles internally), and tears down on unmount so
  // the widget disappears reliably even on navigation/back-swipe.
  useEffect(() => {
    if (!runActive) return;
    if (!RunLiveActivity.isSupported()) return;

    let cancelled = false;
    void RunLiveActivity.start().then(() => {
      if (cancelled) {
        void RunLiveActivity.end();
      }
    });

    return () => {
      cancelled = true;
      void RunLiveActivity.end();
    };
  }, [runActive]);

  // Push the latest stats into the Lock Screen / Dynamic Island. Pace is
  // computed as overall average so far (mm:ss/km). Pause/resume are forced
  // through with `flush` so the widget reacts instantly.
  useEffect(() => {
    if (!runActive) return;
    if (!RunLiveActivity.isSupported()) return;

    const distanceKm = totalDistance / 1000;
    const paceSecondsPerKm =
      elapsedSeconds > 0 && distanceKm > 0.01 ? elapsedSeconds / distanceKm : 0;

    const payload = {
      elapsedSeconds,
      distanceMeters: totalDistance,
      paceSecondsPerKm,
      isPaused,
    };

    void RunLiveActivity.flush(payload);
  }, [runActive, isPaused]);

  useEffect(() => {
    if (!runActive) return;
    if (!RunLiveActivity.isSupported()) return;

    const distanceKm = totalDistance / 1000;
    const paceSecondsPerKm =
      elapsedSeconds > 0 && distanceKm > 0.01 ? elapsedSeconds / distanceKm : 0;

    void RunLiveActivity.update({
      elapsedSeconds,
      distanceMeters: totalDistance,
      paceSecondsPerKm,
      isPaused,
    });
  }, [elapsedSeconds, totalDistance, isPaused, runActive]);

  // Log screen view on mount
  useEffect(() => {
    Analytics.logScreenView('RunningActive');
  }, []);

  // Background audio keep-alive: prevents Android Doze/WebView from suspending JS when screen is off.
  // iOS: SKIPPED entirely. The silent WAV loop is a WebView <audio> element which iOS surfaces as a
  //   "Safari video" Now Playing tile on the lock screen and also competes for the audio session,
  //   pausing Spotify/Apple Music. On iOS we don't need this hack: BackgroundGeolocation's native
  //   background mode keeps the app alive, and the Live Activity is the lock-screen presence.
  // Android: still needed - silent audio keeps app active so location updates and TTS continue.
  useEffect(() => {
    if (!runActive || !Capacitor.isNativePlatform()) return;
    const isIOS = Capacitor.getPlatform() === 'ios';
    if (isIOS) return; // iOS keep-alive disabled - see comment above
    let audio: HTMLAudioElement | null = null;
    let blobUrl: string | null = null;
    let cancelled = false;
    let pauseHandler: (() => void) | null = null;
    const startAudio = () => {
      if (cancelled) return;
      if (audio && !audio.paused) return; // Already playing
      if (audio && audio.paused) {
        if (isIOS) prepareBackgroundKeepAlive();
        const p = audio.play();
        if (p?.catch) p.catch(() => { if (!cancelled) setTimeout(startAudio, 800); });
        return;
      }
      if (isIOS) prepareBackgroundKeepAlive(); // playback + mixWithOthers - plays when locked, music keeps playing
      try {
        const sampleRate = 44100;
        const numChannels = 1;
        const bitsPerSample = 16;
        const duration = 1;
        const dataSize = sampleRate * numChannels * (bitsPerSample / 8) * duration;
        const buffer = new ArrayBuffer(44 + dataSize);
        const view = new DataView(buffer);
        const writeStr = (offset: number, str: string) => {
          for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
        };
        writeStr(0, 'RIFF');
        view.setUint32(4, 36 + dataSize, true);
        writeStr(8, 'WAVE');
        writeStr(12, 'fmt ');
        view.setUint32(16, 16, true);
        view.setUint16(20, 1, true);
        view.setUint16(22, numChannels, true);
        view.setUint32(24, sampleRate, true);
        view.setUint32(28, sampleRate * numChannels * (bitsPerSample / 8), true);
        view.setUint16(32, numChannels * (bitsPerSample / 8), true);
        view.setUint16(34, bitsPerSample, true);
        writeStr(36, 'data');
        view.setUint32(40, dataSize, true);
        view.setInt16(44, 1, true);
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        blobUrl = URL.createObjectURL(new Blob([buffer], { type: 'audio/wav' }));
        audio = new Audio(blobUrl);
        audio.loop = true;
        audio.volume = 0.01;
        const tryPlay = () => {
          if (!audio || cancelled) return;
          const p = audio.play();
          if (p?.catch) {
            p.catch((e: unknown) => {
              if (!cancelled) {
                console.warn('[Run] Background audio play failed, retrying in 800ms:', e);
                setTimeout(tryPlay, 800);
              }
            });
          }
        };
        tryPlay();
        pauseHandler = () => {
          if (cancelled || !audio) return;
          if (audio.ended) return;
          setTimeout(() => {
            if (cancelled || !audio) return;
            tryPlay();
            console.log('[Run] Background audio restart after pause (interruption)');
          }, 900);
        };
        audio.addEventListener('pause', pauseHandler);
        console.log('[Run] Background audio keep-alive started', isIOS ? '(iOS)' : '(Android)');
        // iOS only: briefly release session so Spotify/Apple Music can resume, then reactivate for keep-alive
        if (isIOS) setTimeout(() => resumeMusicAfterRunStart(), 600);
      } catch (e) {
        console.warn('[Run] Could not start background audio:', e);
        if (!cancelled) setTimeout(startAudio, 500);
      }
    };
    startAudio(); // Start immediately - prepareBackgroundKeepAlive called inside startAudio
    const sub = App.addListener('appStateChange', ({ isActive }) => {
      if (cancelled) return;
      if (!isActive) {
        if (isIOS) prepareBackgroundKeepAlive();
        startAudio(); // Fallback: ensure audio runs when user locks
      }
    });
    return () => {
      cancelled = true;
      sub.then((l) => l.remove());
      if (audio && pauseHandler) audio.removeEventListener('pause', pauseHandler);
      try {
        audio?.pause();
        audio = null;
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        blobUrl = null;
        console.log('[Run] Background audio keep-alive stopped');
      } catch {}
    };
  }, [runActive]);

  // Keyboard height for Run Summary - keep Notes visible above keyboard on iOS
  const keyboardListenersRef = useRef<{ remove: () => Promise<void> }[]>([]);
  useEffect(() => {
    if (!showRunSummary || !Capacitor.isNativePlatform()) return;
    let cancelled = false;
    const isIOS = Capacitor.getPlatform() === 'ios';
    const showEvent = isIOS ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent = isIOS ? 'keyboardWillHide' : 'keyboardDidHide';
    void Promise.all([
      Keyboard.addListener(showEvent as 'keyboardWillShow', (info: { keyboardHeight: number }) => {
        if (!cancelled) setKeyboardHeight(info.keyboardHeight);
      }),
      Keyboard.addListener(hideEvent as 'keyboardWillHide', () => {
        if (!cancelled) setKeyboardHeight(0);
      }),
    ]).then(([show, hide]) => {
      if (!cancelled) {
        keyboardListenersRef.current = [show, hide];
      } else {
        show.remove();
        hide.remove();
      }
    });
    return () => {
      cancelled = true;
      setKeyboardHeight(0);
      keyboardListenersRef.current.forEach((l) => l.remove());
      keyboardListenersRef.current = [];
    };
  }, [showRunSummary]);

  // Lock screen / Control Center: "Straiv - Run in progress" (only during active run, not when summary/analyze shown)
  // Refresh every 3s - TTS overwrites Now Playing when it speaks, so we re-apply to keep our title
  useEffect(() => {
    const shouldShow = runActive && !showRunSummary && !showAnalyzePrompt;
    nowPlayingActiveRef.current = shouldShow;
    setStraivNowPlaying(shouldShow);
    if (!shouldShow) return () => setStraivNowPlaying(false);
    const interval = setInterval(() => setStraivNowPlaying(true), 3000);
    return () => {
      clearInterval(interval);
      nowPlayingActiveRef.current = false;
      setStraivNowPlaying(false);
    };
  }, [runActive, showRunSummary, showAnalyzePrompt]);
  
  // Restore session from storage on mount (for background persistence)
  useEffect(() => {
    const savedSession = localStorage.getItem('activeRunningSession');
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        if (Date.now() - session.timestamp < 2 * 60 * 60 * 1000) {
          console.log('[Run] Restoring session from background:', session);
          const segments = session.routeSegments || (session.routePoints?.length ? [session.routePoints] : [[]]);
          setRouteSegments(segments);
          const pts = segments.flat();
          if (pts.length > 0) {
            const last = pts[pts.length - 1];
            setCurrentPosition([last.lat, last.lng]);
          }
          setElapsedSeconds(session.elapsedSeconds || 0);
          setTotalDistance(session.totalDistance || 0);
          setPauseCount(session.pauseCount || 0);
          setIsPaused(session.isPaused || false);
          startTimeRef.current = session.startTime || Date.now();
          pausedTimeRef.current = session.pausedTime || 0;
          lastSplitKmRef.current = session.lastSplitKm || 0;
          splitTimesRef.current = session.splitTimes || [];
          if (session.settings) setSettings(session.settings);
          Analytics.runningSessionRestored(session.elapsedSeconds || 0, session.totalDistance || 0);
        } else {
          localStorage.removeItem('activeRunningSession');
          Analytics.runningSessionExpired(Math.round((Date.now() - session.timestamp) / 60000));
          startTimeRef.current = Date.now();
        }
      } catch (e) {
        console.warn('Failed to restore running session:', e);
        startTimeRef.current = Date.now();
      }
    } else {
      startTimeRef.current = Date.now();
    }
  }, []);

  // Text-to-speech announcement - use native plugin on iOS and Android (more reliable than Web Speech API)
  // iOS: TTS uses ambient + mixWithOthers so music keeps playing without ducking or pausing.
  // Android: native TTS works without audio session config.
  // The duckOthers option is kept for backwards-compat with callers but ignored.
  const announce = useCallback(async (text: string, _options?: { duckOthers?: boolean }) => {
    if (!audioEnabled || settings.audioStats === 'off' || typeof window === 'undefined') return;
    
    try {
      if (Capacitor.isNativePlatform()) {
        const sanitized = String(text || '')
          .replace(/[<>&"'`{}\[\]\\|@#$%^*+=~:;\/]/g, ' ')
          .replace(/[''\u2018\u2019\u201C\u201D]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        if (!sanitized) return;
        const voiceIndex = await getTTSVoiceIndex(settings.audioVoice || 'female');
        await TextToSpeech.stop();
        const speakOpts: Record<string, unknown> = {
          text: sanitized,
          lang: 'en-US',
          rate: 0.95,
          pitch: 1.0,
          volume: 1.0,
          queueStrategy: 1,
          duckOthers: true,
          category: 'playback',
          ...(voiceIndex !== undefined && voiceIndex >= 0 && { voice: voiceIndex }),
        };
        // iOS: do NOT force playback category or duckOthers. Those route through a custom
        // .playback + .setActive(true) path in the native plugin which makes iOS show a
        // "Now Playing" lock-screen tile and pauses Spotify/Apple Music. Default ambient +
        // mixWithOthers lets TTS overlay on top of music without interrupting it.
        await TextToSpeech.speak(speakOpts as unknown as Parameters<typeof TextToSpeech.speak>[0]);
      } else if ('speechSynthesis' in window && window.speechSynthesis) {
        let sanitized = String(text || '')
          .replace(/[<>&"'`{}\[\]\\|@#$%^*+=~:;\/]/g, ' ')
          .replace(/[''\u2018\u2019\u201C\u201D]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        if (!sanitized) return;
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(sanitized);
        utterance.rate = 0.95;
        utterance.volume = 1.0;
        // Use the device/app locale so the TTS engine picks the right voice
        // (e.g. a German voice will pronounce "Kilometer" correctly). We still
        // fall back to en-US if i18n hasn't loaded yet. Note: the spoken text
        // itself is currently English-only - localizing the TTS phrases is a
        // follow-up task tracked separately.
        const ttsLang = (typeof navigator !== 'undefined' && navigator.language) || 'en-US';
        utterance.lang = ttsLang;
        const voicePref = settings.audioVoice || 'female';
        const voices = window.speechSynthesis.getVoices();
        if (voices.length > 0) {
          let selectedVoice: SpeechSynthesisVoice | null = null;
          const premiumVoiceNames = ['enhanced', 'premium', 'neural', 'natural'];
          if (voicePref === 'male') {
            selectedVoice = voices.find(v => 
              v.lang.startsWith('en') && 
              premiumVoiceNames.some(p => v.name.toLowerCase().includes(p)) &&
              (v.name.toLowerCase().includes('male') || 
               v.name.toLowerCase().includes('daniel') ||
               v.name.toLowerCase().includes('alex'))
            ) || voices.find(v => 
              v.lang.startsWith('en') && 
              (v.name.toLowerCase().includes('daniel') ||
               v.name.toLowerCase().includes('alex') ||
               v.name.toLowerCase().includes('thomas') ||
               v.name.toLowerCase().includes('fred') ||
               v.name.toLowerCase().includes('male'))
            );
            utterance.pitch = 0.9;
          } else {
            selectedVoice = voices.find(v => 
              v.lang.startsWith('en') && 
              premiumVoiceNames.some(p => v.name.toLowerCase().includes(p)) &&
              (v.name.toLowerCase().includes('samantha') ||
               v.name.toLowerCase().includes('karen') ||
               v.name.toLowerCase().includes('female'))
            ) || voices.find(v => 
              v.lang.startsWith('en') && 
              (v.name.toLowerCase().includes('samantha') ||
               v.name.toLowerCase().includes('victoria') ||
               v.name.toLowerCase().includes('karen') ||
               v.name.toLowerCase().includes('moira') ||
               v.name.toLowerCase().includes('female'))
            );
            utterance.pitch = 1.05;
          }
          if (selectedVoice) utterance.voice = selectedVoice;
        }
        utterance.onerror = (event) => console.warn('[Audio] Speech synthesis error:', event.error);
        window.speechSynthesis.speak(utterance);
      } else {
        console.log('[Audio] Speech synthesis not available');
      }
    } catch (error) {
      console.warn('[Audio] Failed to announce:', error);
    }
  }, [audioEnabled, settings.audioStats, settings.audioVoice]);
  
  // Helper to convert time to natural speech (e.g., "2 minutes and 30 seconds")
  const formatTimeForSpeech = useCallback((totalSeconds: number): string => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    const parts: string[] = [];
    
    if (hours > 0) {
      parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
    }
    if (minutes > 0) {
      parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
    }
    if (seconds > 0 || parts.length === 0) {
      parts.push(`${seconds} second${seconds !== 1 ? 's' : ''}`);
    }
    
    if (parts.length === 1) return parts[0];
    if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
    return `${parts[0]}, ${parts[1]}, and ${parts[2]}`;
  }, []);
  
  // Announce stats with natural, conversational speech
  const announceStats = useCallback(() => {
    const distanceKm = totalDistance / 1000;
    const paceSeconds = elapsedSeconds > 0 && distanceKm > 0 
      ? elapsedSeconds / distanceKm 
      : 0;
    
    // Natural distance announcement
    let distanceText: string;
    if (distanceKm >= 1) {
      const wholeKm = Math.floor(distanceKm);
      const decimal = Math.round((distanceKm - wholeKm) * 100);
      if (decimal === 0) {
        distanceText = `${wholeKm} kilometer${wholeKm !== 1 ? 's' : ''}`;
      } else {
        distanceText = `${wholeKm} point ${decimal < 10 ? 'oh ' : ''}${decimal.toString().split('').join(' ')} kilometers`;
      }
    } else {
      distanceText = `${Math.round(totalDistance)} meters`;
    }
    
    // Natural time announcement (e.g., "12 minutes and 30 seconds")
    const timeText = formatTimeForSpeech(elapsedSeconds);
    
    // Convert pace to spoken format - avoid "0 minutes" (iOS SSML issues)
    const formatPaceForSpeech = (paceInSeconds: number): string => {
      if (!paceInSeconds || !isFinite(paceInSeconds)) return '';
      const mins = Math.floor(paceInSeconds / 60);
      const secs = Math.round(paceInSeconds % 60);
      if (mins === 0) return `${secs} second${secs !== 1 ? 's' : ''}`;
      if (secs === 0) return `${mins} minute${mins !== 1 ? 's' : ''}`;
      return `${mins} minute${mins !== 1 ? 's' : ''} and ${secs} seconds`;
    };
    
    // Simple format to avoid iOS SSML parse errors (no apostrophes, minimal punctuation)
    let announcement = `You have covered ${distanceText} in ${timeText}`;
    if (paceSeconds > 0 && isFinite(paceSeconds)) {
      announcement += `. Average pace ${formatPaceForSpeech(paceSeconds)} per kilometer`;
    }
    announcement += '.';
    announce(announcement, { duckOthers: true });
  }, [totalDistance, elapsedSeconds, announce, formatTimeForSpeech]);
  
  const isTimeBasedInterval = ['1min', '5min', '10min'].includes(settings.audioStats);
  const isDistanceBasedInterval = ['500m', '1km', '5km'].includes(settings.audioStats);
  const distanceIntervalMeters = settings.audioStats === '500m' ? 500 : settings.audioStats === '1km' ? 1000 : settings.audioStats === '5km' ? 5000 : 0;

  // Keep ref updated so interval callback always has latest (avoids resetting interval every second)
  announceStatsRef.current = announceStats;

  // Kilometer milestone announcements - only when using time-based interval (avoid duplicate with distance interval)
  useEffect(() => {
    if (isDistanceBasedInterval) return;
    const currentKm = Math.floor(totalDistance / 1000);
    if (currentKm > lastAnnouncementKmRef.current && currentKm > 0) {
      lastAnnouncementKmRef.current = currentKm;
      
      // Calculate split pace for this km
      const currentElapsed = elapsedSecondsRef.current;
      const prevSplit = splitTimesRef.current.find(s => s.km === currentKm - 1);
      const splitTime = prevSplit ? currentElapsed - prevSplit.elapsedSeconds : currentElapsed;
      const splitMins = Math.floor(splitTime / 60);
      const splitSecs = splitTime % 60;
      
      // Log kilometer milestone
      Analytics.runningKilometerCompleted(currentKm, splitTime, currentElapsed);
      Analytics.runningAudioAnnouncementPlayed('km_milestone');
      
      // Simple format to avoid iOS SSML parse errors (avoid "0 minutes", complex punctuation)
      let message = `${currentKm} kilometer${currentKm > 1 ? 's' : ''} done`;
      if (splitTime > 0 && splitTime < 1800) {
        if (splitMins > 0) {
          message += ` in ${splitMins} minute${splitMins !== 1 ? 's' : ''}${splitSecs > 0 ? ` and ${splitSecs} seconds` : ''}`;
        } else {
          message += ` in ${splitSecs} seconds`;
        }
      }
      message += '.';
      announce(message, { duckOthers: true });
    }
  }, [totalDistance, announce, isDistanceBasedInterval]);
  
  // Timed audio announcements (every 1/5/10 min) - only when time-based interval
  // Uses ref for announceStats so interval is NOT reset every second (elapsedSeconds changes)
  useEffect(() => {
    if (!isTimeBasedInterval || settings.audioStats === 'off' || !audioEnabled || isPaused || !runActive) {
      if (audioIntervalRef.current) {
        clearInterval(audioIntervalRef.current);
        audioIntervalRef.current = null;
      }
      return;
    }
    
    const intervalMinutes = parseInt(settings.audioStats, 10) || 5;
    const intervalMs = intervalMinutes * 60 * 1000;
    
    audioIntervalRef.current = setInterval(() => {
      if (runActive && !isPaused) {
        // Skip if user recently triggered manual announce (prevents cutting off button-triggered speech)
        if (Date.now() - lastManualAnnounceTimeRef.current < 90000) return;
        announceStatsRef.current();
      }
    }, intervalMs);
    
    return () => {
      if (audioIntervalRef.current) {
        clearInterval(audioIntervalRef.current);
      }
    };
  }, [settings.audioStats, audioEnabled, isPaused, runActive, isTimeBasedInterval]);
  
  // Distance-based audio announcements (every 500m / 1km / 5km)
  useEffect(() => {
    if (!isDistanceBasedInterval || distanceIntervalMeters <= 0 || !audioEnabled || isPaused || !runActive) return;
    while (totalDistance >= lastDistanceIntervalRef.current + distanceIntervalMeters) {
      lastDistanceIntervalRef.current += distanceIntervalMeters;
      announceStats();
      Analytics.runningAudioAnnouncementPlayed('stats');
    }
  }, [totalDistance, isDistanceBasedInterval, distanceIntervalMeters, audioEnabled, isPaused, runActive, announceStats]);
  
  // Timer - updates both state and ref for accurate split recording (only when run active post-countdown)
  useEffect(() => {
    if (runActive && !isPaused) {
      timerRef.current = setInterval(() => {
        const now = Date.now();
        const elapsed = Math.floor((now - startTimeRef.current - pausedTimeRef.current) / 1000);
        setElapsedSeconds(elapsed);
        elapsedSecondsRef.current = elapsed; // Keep ref in sync for callbacks
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [runActive, isPaused]);

  // GPS tracking - BackgroundGeolocation on native (works when screen locked), fallback to standard
  useEffect(() => {
    if (!runActive) return;
    const isNative = Capacitor.isNativePlatform();
    let isMounted = true;
    
    const processLocation = (lat: number, lng: number, accuracy: number, timestamp: number) => {
      if (!isMounted) return;
      
      const newPoint: GpsPoint = {
        lat,
        lng,
        timestamp: timestamp || Date.now(),
        accuracy,
      };
      
      if (newPoint.accuracy) {
        if (newPoint.accuracy <= 5) setGpsSignal('strong');
        else if (newPoint.accuracy <= 15) setGpsSignal('medium');
        else if (newPoint.accuracy <= 30) setGpsSignal('weak');
        else setGpsSignal('searching');
      }
      
      if (newPoint.accuracy && newPoint.accuracy > 30) return;
      
      setCurrentPosition([newPoint.lat, newPoint.lng]);
      
      if (lastPositionRef.current) {
        const dist = calculateDistance(
          lastPositionRef.current.lat,
          lastPositionRef.current.lng,
          newPoint.lat,
          newPoint.lng
        );
        
        if (dist > 3) {
          setTotalDistance(prev => {
            const newTotal = prev + dist;
            const newTotalKm = newTotal / 1000;
            const currentKm = Math.floor(newTotalKm);
            if (currentKm > lastSplitKmRef.current && currentKm > 0) {
              const currentElapsed = Math.floor((Date.now() - startTimeRef.current - pausedTimeRef.current) / 1000);
              lastSplitKmRef.current = currentKm;
              splitTimesRef.current.push({ km: currentKm, elapsedSeconds: currentElapsed });
            }
            return newTotal;
          });
          setRouteSegments(prev => {
            const last = prev[prev.length - 1];
            return [...prev.slice(0, -1), [...last, newPoint]];
          });
          lastPositionRef.current = newPoint;
        }
      }
    };
    
    const startTracking = async () => {
      try {
        const permission = await Geolocation.requestPermissions();
        if (!isMounted) return;
        if (permission.location !== 'granted') {
          // User passed check on RunningStart - if we get here, permission was revoked; no toast (they have Always)
          return;
        }
        
        const quickPosition = await Geolocation.getCurrentPosition({
          enableHighAccuracy: false,
          timeout: 3000,
          maximumAge: 30000,
        }).catch(() => null);
        
        if (isMounted && quickPosition) {
          const quickPoint: GpsPoint = {
            lat: quickPosition.coords.latitude,
            lng: quickPosition.coords.longitude,
            timestamp: quickPosition.timestamp,
            accuracy: quickPosition.coords.accuracy,
          };
          setCurrentPosition([quickPoint.lat, quickPoint.lng]);
          setGpsSignal('weak');
        }
        
        let initialPosition: Position | null = null;
        try {
          initialPosition = await Geolocation.getCurrentPosition({
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 5000,
          });
        } catch (posErr) {
          if (quickPosition && isMounted) {
            const fallback: GpsPoint = {
              lat: quickPosition.coords.latitude,
              lng: quickPosition.coords.longitude,
              timestamp: quickPosition.timestamp,
              accuracy: quickPosition.coords.accuracy,
            };
            const existingSegments = sessionDataRef.current?.routeSegments ?? (sessionDataRef.current?.routePoints ? [sessionDataRef.current.routePoints] : [[]]);
            const isResuming = existingSegments.flat().length > 0;
            if (!isResuming) {
              setRouteSegments(prev => (prev.length === 0 || (prev[prev.length - 1]?.length ?? 0) === 0 ? [[fallback]] : [...prev.slice(0, -1), [...(prev[prev.length - 1] || []), fallback]]));
              lastPositionRef.current = fallback;
            } else {
              lastPositionRef.current = fallback; // Use current position when resuming
            }
          }
        }
        
        if (!isMounted) return;
        
        // Android 13+: Request notification permission so foreground service notification can show
        // (Required for GPS/splits to work when screen is off - Doze won't suspend us)
        if (isNative && Capacitor.getPlatform() === 'android' && typeof Notification !== 'undefined' && Notification.permission === 'default') {
          try {
            await Notification.requestPermission();
          } catch (_) {}
        }
        
        // When resuming: set lastPositionRef to CURRENT position so we don't add "walk during pause" to route
        const existingSegments = sessionDataRef.current?.routeSegments ?? (sessionDataRef.current?.routePoints ? [sessionDataRef.current.routePoints] : [[]]);
        const allPoints = existingSegments.flat();
        const isResuming = allPoints.length > 0;
        
        if (initialPosition) {
          const initialPoint: GpsPoint = {
            lat: initialPosition.coords.latitude,
            lng: initialPosition.coords.longitude,
            timestamp: initialPosition.timestamp,
            accuracy: initialPosition.coords.accuracy,
          };
          setCurrentPosition([initialPoint.lat, initialPoint.lng]);
          if (isResuming) {
            // Set lastPositionRef to CURRENT position - next points added only when user moves 3m from here
            // This excludes movement during pause from the route
            lastPositionRef.current = initialPoint;
          } else {
            setRouteSegments(prev => {
              const last = prev[prev.length - 1];
              if (last.length === 0) return [[initialPoint]];
              return [...prev.slice(0, -1), [...last, initialPoint]];
            });
            lastPositionRef.current = initialPoint;
          }
        } else if (isResuming && quickPosition && isMounted) {
          lastPositionRef.current = {
            lat: quickPosition.coords.latitude,
            lng: quickPosition.coords.longitude,
            timestamp: quickPosition.timestamp,
            accuracy: quickPosition.coords.accuracy ?? 50,
          };
        } else if (isResuming && allPoints.length > 0) {
          lastPositionRef.current = allPoints[allPoints.length - 1];
        }
        
        if (!hasAnnouncedStartRef.current) {
          hasAnnouncedStartRef.current = true;
          if (isMounted) {
            announce(
              t(
                gpsKind === 'walk'
                  ? 'running.gpsActivity.sessionStarted_walk'
                  : gpsKind === 'hike'
                    ? 'running.gpsActivity.sessionStarted_hike'
                    : 'running.gpsActivity.sessionStarted_run'
              ),
              { duckOthers: true }
            );
            Analytics.runningAudioAnnouncementPlayed('start');
          }
        }
        
        if (isNative && !isPaused) {
          try {
            const bgGeo = getBackgroundGeolocation();
            if (bgGeo) {
              const watcherId = await bgGeo.addWatcher(
                {
                  backgroundMessage: t('running.active.notification.body'),
                  backgroundTitle: t('running.active.notification.title'),
                  requestPermissions: true,
                  distanceFilter: 3,
                  stale: false,
                },
                (location: { latitude: number; longitude: number; accuracy?: number; time?: number } | null, error: any) => {
                  if (error) {
                    // No toast - user passed Always check on RunningStart
                    return console.warn('[GPS] Background error:', error);
                  }
                  if (location && isMounted) {
                    processLocation(location.latitude, location.longitude, location.accuracy ?? 50, location.time ?? Date.now());
                  }
                }
              );
              if (isMounted) {
                bgWatcherIdRef.current = watcherId;
                console.log('[GPS] Background watcher started');
              }
            } else {
              throw new Error('Plugin not available');
            }
          } catch (bgErr: any) {
            const errMsg = bgErr?.message ?? String(bgErr);
            const errCode = bgErr?.code ?? bgErr?.name;
            console.warn('[GPS] Background geolocation unavailable, using standard:', { message: errMsg, code: errCode, err: bgErr });
            // No toast - user passed Always check on RunningStart; fall back to standard Geolocation
            const watchId = await Geolocation.watchPosition(
              { enableHighAccuracy: true, timeout: 10000, maximumAge: 1000 },
              (position: Position | null, err) => {
                if (err || !position || !isMounted) return;
                processLocation(position.coords.latitude, position.coords.longitude, position.coords.accuracy ?? 50, position.timestamp);
              }
            );
            if (isMounted) watchIdRef.current = watchId;
          }
        } else {
          const watchId = await Geolocation.watchPosition(
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 1000 },
            (position: Position | null, err) => {
              if (err || !position || !isMounted) return;
              if (isPaused) return;
              processLocation(position.coords.latitude, position.coords.longitude, position.coords.accuracy ?? 50, position.timestamp);
            }
          );
          if (isMounted) watchIdRef.current = watchId;
        }
      } catch (error: any) {
        console.error('[GPS] Failed to start tracking:', error);
        if (isMounted) toast.error(t('running.active.toast.gpsError', { message: error.message || 'Unknown error' }));
      }
    };
    
    if (runActive && !isPaused) {
      startTracking();
    }
    
    return () => {
      isMounted = false;
      if (bgWatcherIdRef.current) {
        getBackgroundGeolocation()?.removeWatcher({ id: bgWatcherIdRef.current }).catch(console.warn);
        bgWatcherIdRef.current = null;
      }
      if (watchIdRef.current) {
        Geolocation.clearWatch({ id: watchIdRef.current }).catch(console.warn);
        watchIdRef.current = null;
      }
    };
  }, [runActive, isPaused, announce, navigate, gpsRestartKey, t, gpsKind]);
  
  // Track pause duration for analytics
  const pauseStartTimeRef = useRef<number>(0);
  
  // Handle pause - start new segment so movement during pause is not added to route
  const handlePause = () => {
    if (isLocked) return;
    
    const newPauseCount = pauseCount + 1;
    setIsPaused(true);
    setPauseCount(newPauseCount);
    pausedTimeRef.current = Date.now() - startTimeRef.current - (elapsedSeconds * 1000);
    pauseStartTimeRef.current = Date.now(); // Track when pause started for analytics
    announce(t('running.gpsActivity.sessionPaused'), { duckOthers: true });
    
    // Start new segment - movement during pause will go here but we clear it on resume
    setRouteSegments(prev => [...prev, []]);
    
    // Log pause event
    Analytics.runningWorkoutPaused(elapsedSeconds, totalDistance, newPauseCount);
    Analytics.runningAudioAnnouncementPlayed('pause');
    
    if (watchIdRef.current) {
      Geolocation.clearWatch({ id: watchIdRef.current });
      watchIdRef.current = null;
    }
    if (bgWatcherIdRef.current) {
      getBackgroundGeolocation()?.removeWatcher({ id: bgWatcherIdRef.current }).catch(console.warn);
      bgWatcherIdRef.current = null;
    }
  };
  
  // Handle resume - empty segment from pause will receive new run points (no line from pause→resume)
  const handleResume = () => {
    if (isLocked) return;
    
    const pauseDuration = pauseStartTimeRef.current > 0 
      ? Math.round((Date.now() - pauseStartTimeRef.current) / 1000)
      : 0;
    
    setIsPaused(false);
    announce(t('running.gpsActivity.sessionResumed'), { duckOthers: true });
    
    // Log resume event
    Analytics.runningWorkoutResumed(elapsedSeconds, totalDistance, pauseDuration);
    Analytics.runningAudioAnnouncementPlayed('resume');
  };
  
  // Track when stop dialog opened for paused time calculation
  const stopDialogOpenedRef = useRef<number>(0);
  
  // Handle stop button - show confirmation and pause timer
  const handleStopClick = () => {
    if (isLocked) return;
    // Save the time when dialog opened
    stopDialogOpenedRef.current = Date.now();
    // Pause the timer while showing confirmation
    setIsPaused(true);
    setShowStopConfirm(true);
    
    // Log stop dialog opened
    Analytics.runningStopDialogOpened();
  };
  
  // Cancel stop - resume the run and account for paused time
  const handleCancelStop = () => {
    // Don't do anything if stop was already confirmed
    if (stopConfirmedRef.current) {
      stopConfirmedRef.current = false;
      return;
    }
    
    setShowStopConfirm(false);
    // Add the time spent in dialog to paused time
    if (stopDialogOpenedRef.current > 0) {
      const dialogTime = Date.now() - stopDialogOpenedRef.current;
      pausedTimeRef.current += dialogTime;
      stopDialogOpenedRef.current = 0;
    }
    setIsPaused(false);
    announce(t('running.gpsActivity.sessionResumed'), { duckOthers: true });
    
    // Log stop dialog cancelled
    Analytics.runningStopDialogCancelled();
  };
  
  // Confirm stop and finish workout
  const handleConfirmStop = async () => {
    // Mark that stop was confirmed (to prevent "run resumed" from playing)
    stopConfirmedRef.current = true;
    
    // Log stop confirmed
    Analytics.runningStopDialogConfirmed();
    Analytics.runningWorkoutStopped(elapsedSeconds, totalDistance, true);

    // Tear down the Lock Screen Live Activity immediately so the widget
    // disappears the moment the user stops (rather than after summary save).
    void RunLiveActivity.end();

    setShowStopConfirm(false);
    setIsRunning(false);
    setIsPaused(true);
    
    if (watchIdRef.current) {
      Geolocation.clearWatch({ id: watchIdRef.current });
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    const distanceKm = totalDistance / 1000;
    const paceSeconds = elapsedSeconds > 0 && distanceKm > 0 
      ? elapsedSeconds / distanceKm 
      : 0;
    const durationMinutes = Math.round(elapsedSeconds / 60);
    
    // Calculate calories using MET formula based on user weight
    // Running MET ≈ 9.8 (moderate pace), adjusted by speed
    // Formula: Calories = MET × weight(kg) × time(hours)
    const userWeightKg = profile?.weight_kg || 70; // Default 70kg if not set
    const avgSpeedKmh = elapsedSeconds > 0 
      ? distanceKm / (elapsedSeconds / 3600)
      : 0;
    
    // MET: running pace bands; walking/hiking use lower bases (ACSM-style approximations)
    let met = 8.0;
    if (gpsKind === 'walk') {
      if (avgSpeedKmh >= 6.5) met = 5.0;
      else if (avgSpeedKmh >= 5) met = 3.8;
      else met = 3.3;
    } else if (gpsKind === 'hike') {
      if (avgSpeedKmh >= 5.5) met = 9.0;
      else if (avgSpeedKmh >= 4) met = 7.0;
      else met = 6.0;
    } else {
      if (avgSpeedKmh >= 14) met = 13.5;
      else if (avgSpeedKmh >= 12) met = 11.5;
      else if (avgSpeedKmh >= 10) met = 9.8;
      else if (avgSpeedKmh >= 8) met = 8.3;
      else if (avgSpeedKmh >= 6) met = 6.0;
    }
    
    const timeHours = elapsedSeconds / 3600;
    const estimatedCalories = Math.round(met * userWeightKg * timeHours);
    
    // Create comprehensive workout summary for Arnold (matching Chat.tsx expected format)
    const summary = {
      type: 'running',
      title: defaultTitleWithKm(distanceKm),
      duration: durationMinutes,
      distance: parseFloat(distanceKm.toFixed(2)),
      pace: formatPace(paceSeconds),
      // Notes will be added when user submits (can include user notes)
      notes: '',
      // Extended data for better analysis
      averageSpeedKmh: avgSpeedKmh.toFixed(1),
      estimatedCalories: estimatedCalories,
      pauseCount: pauseCount,
      gpsPointsRecorded: routePoints.length,
      userWeight: userWeightKg,
      userAge: profile?.birth_date ? new Date().getFullYear() - new Date(profile.birth_date).getFullYear() : undefined,
      userHeight: profile?.height_cm,
      startTime: new Date(startTimeRef.current).toISOString(),
      endTime: new Date().toISOString(),
      startLocation: routePoints.length > 0 ? {
        lat: routePoints[0].lat,
        lng: routePoints[0].lng
      } : null,
      endLocation: routePoints.length > 0 ? {
        lat: routePoints[routePoints.length - 1].lat,
        lng: routePoints[routePoints.length - 1].lng
      } : null,
      _ts: Date.now() // Timestamp for freshness check
    };
    
    setWorkoutSummary(summary);
    
    // Calculate km splits with accurate pace per kilometer
    const splits: { km: number; time: string; pace: string; isPartial?: boolean }[] = [];
    let prevSeconds = 0;
    
    // Get the number of full kilometers completed
    const fullKilometers = Math.floor(distanceKm);
    
    console.log('[Splits] Calculating - distanceKm:', distanceKm, 'fullKilometers:', fullKilometers, 'splitTimesRef:', splitTimesRef.current);
    
    // Add recorded splits (full kilometers completed)
    for (const split of splitTimesRef.current) {
      const splitSeconds = split.elapsedSeconds - prevSeconds;
      const pacePerKm = formatPace(splitSeconds); // seconds per km
      splits.push({
        km: split.km,
        time: formatTime(splitSeconds), // Time for THIS split segment
        pace: pacePerKm
      });
      prevSeconds = split.elapsedSeconds;
    }
    // Ensure every full km is shown (fill gaps if any splits were missed)
    for (let km = 1; km <= fullKilometers; km++) {
      if (!splits.some(s => s.km === km)) {
        const avgPace = distanceKm > 0 ? elapsedSeconds / distanceKm : 0;
        splits.push({ km, time: formatTime(Math.round(avgPace)), pace: formatPace(avgPace) });
      }
    }
    splits.sort((a, b) => a.km - b.km);
    
    // Calculate the partial/remaining distance after the last full km
    const remainingKm = distanceKm - fullKilometers;
    const lastRecordedSplitTime = splits.length > 0 ? prevSeconds : 0;
    const remainingSeconds = elapsedSeconds - lastRecordedSplitTime;
    
    console.log('[Splits] Remaining - remainingKm:', remainingKm, 'remainingSeconds:', remainingSeconds);
    
    // Add the partial kilometer if there's remaining distance (more than 10 meters)
    if (remainingKm >= 0.01 && remainingSeconds > 0) {
      // Extrapolate pace to per-km for comparison
      const estimatedPacePerKm = remainingSeconds / remainingKm;
      splits.push({
        km: Math.round(remainingKm * 100) / 100, // Round to 2 decimal places (e.g., 0.49)
        time: formatTime(remainingSeconds),
        pace: formatPace(estimatedPacePerKm),
        isPartial: true
      });
    } else if (splits.length === 0 && distanceKm > 0) {
      // Run was less than 1km total - show the full distance
      const avgPace = elapsedSeconds / distanceKm;
      splits.push({
        km: Math.round(distanceKm * 100) / 100,
        time: formatTime(elapsedSeconds),
        pace: formatPace(avgPace),
        isPartial: true
      });
    }
    
    console.log('[Splits] Final result:', splits);
    setKmSplits(splits);
    
    // Set default run title with date
    const today = new Date();
    const dateStr = today.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    setRunTitle(defaultTitleWithDate(dateStr));
    
    // Reset feeling emoji
    setFeelingEmoji("");
    setRunNotes("");
    
    // Natural speech for run summary (use "point" not "dot" for decimals)
    let distanceSpeech: string;
    if (distanceKm >= 1) {
      const wholeKm = Math.floor(distanceKm);
      const decimal = Math.round((distanceKm - wholeKm) * 100);
      distanceSpeech = decimal === 0
        ? `${wholeKm} kilometer${wholeKm !== 1 ? 's' : ''}`
        : `${wholeKm} point ${decimal < 10 ? 'oh ' : ''}${decimal.toString().split('').join(' ')} kilometers`;
    } else {
      distanceSpeech = `${Math.round(totalDistance)} meters`;
    }
    announce(
      t(
        gpsKind === 'walk'
          ? 'running.gpsActivity.finishSpeech_walk'
          : gpsKind === 'hike'
            ? 'running.gpsActivity.finishSpeech_hike'
            : 'running.gpsActivity.finishSpeech_run',
        {
          distance: distanceSpeech,
          time: formatTimeForSpeech(elapsedSeconds),
        }
      ),
      { duckOthers: true }
    );
    Analytics.runningAudioAnnouncementPlayed('finish');
    
    // Log comprehensive workout completion analytics
    Analytics.runningWorkoutCompleted({
      distanceKm: parseFloat(distanceKm.toFixed(2)),
      durationSeconds: elapsedSeconds,
      avgPace: formatPace(paceSeconds),
      calories: estimatedCalories,
      pauseCount: pauseCount,
      gpsPointsCount: routePoints.length,
      splitsCount: splits.length
    });
    
    // Also log the generic workout completed event for backwards compatibility
    Analytics.workoutCompleted('running', {
      duration_minutes: durationMinutes,
      distance_km: distanceKm,
      is_gps_tracked: true,
      pause_count: pauseCount,
      calories: estimatedCalories
    });
    
    // Log summary opened
    Analytics.runningSummaryOpened(distanceKm, elapsedSeconds);
    
    // Show run summary dialog (step 1)
    setShowRunSummary(true);
  };
  
  // Feeling icons (using Lucide icons instead of emojis)
  const feelingOptions = useMemo(() => [
    { id: 'exhausted', icon: Frown, label: t('running.summary.feeling.exhausted'), color: 'text-red-400' },
    { id: 'tough', icon: Meh, label: t('running.summary.feeling.tough'), color: 'text-orange-400' },
    { id: 'good', icon: Smile, label: t('running.summary.feeling.good'), color: 'text-yellow-400' },
    { id: 'strong', icon: Zap, label: t('running.summary.feeling.strong'), color: 'text-primary' },
    { id: 'amazing', icon: Flame, label: t('running.summary.feeling.amazing'), color: 'text-primary' },
  ], [t]);
  
  // Handle save run summary (step 1 → step 2) - also save to workout history
  const handleSaveRunSummary = async () => {
    setIsSavingRun(true);

    // Log summary saved with details
    const todayTitle = defaultTitleWithDate(
      new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    );
    Analytics.runningSummarySaved(
      runNotes.trim().length > 0,
      feelingEmoji.length > 0,
      runTitle.trim() !== todayTitle
    );

    // Clear the active running session from localStorage
    localStorage.removeItem('activeRunningSession');

    let saveSucceeded = false;

    try {
      const workoutTitle =
        runTitle.trim() ||
        defaultTitleWithKm(Number(workoutSummary?.distance) || 0);
      const workoutNotes = [
        runNotes.trim(),
        feelingEmoji ? `Feeling: ${feelingOptions.find(f => f.id === feelingEmoji)?.label || ''}` : '',
        kmSplits.length > 0 ? `Splits: ${kmSplits.map(s => `${s.km}km: ${s.pace}/km`).join(', ')}` : ''
      ].filter(Boolean).join('\n');

      const { data: workout, error: workoutError } = await createWorkout({
        title: workoutTitle,
        description: workoutDescriptionLine(
          Number(workoutSummary?.distance) || 0,
          formatTime(elapsedSeconds)
        ),
        workout_type: gpsWorkoutType,
        duration_minutes: Math.round(elapsedSeconds / 60),
        calories_burned: workoutSummary?.estimatedCalories || 0,
        difficulty_level: feelingOptions.find(f => f.id === feelingEmoji)?.label || 'Moderate',
        notes: workoutNotes,
        started_at: new Date(startTimeRef.current).toISOString(),
        completed_at: new Date().toISOString(),
      });

      if (workoutError || !workout?.id) {
        console.error('Error creating workout:', workoutError);
        toast.error(t('running.summary.toast.saveFailedConnection'));
        setIsSavingRun(false);
        return;
      }

      const { error: exerciseError } = await addExerciseToWorkout(workout.id, {
        name: gpsExerciseName,
        muscle_groups: ['legs', 'cardiovascular'],
        category: 'Cardio',
        duration_seconds: Math.max(1, elapsedSeconds),
        distance_km: workoutSummary?.distance ?? 0,
        notes: JSON.stringify({
          pace: workoutSummary?.pace,
          splits: kmSplits,
          routePoints: routePoints.length > 0 ? (() => {
            const maxPoints = 200;
            if (routePoints.length <= maxPoints) return routePoints;
            const step = (routePoints.length - 1) / (maxPoints - 1);
            const sampled: typeof routePoints = [];
            for (let i = 0; i < maxPoints - 1; i++) {
              sampled.push(routePoints[Math.round(i * step)]);
            }
            sampled.push(routePoints[routePoints.length - 1]);
            return sampled;
          })() : [],
          pauseCount: pauseCount,
          avgSpeedKmh: workoutSummary?.averageSpeedKmh,
          feeling: feelingEmoji,
        }),
      });

      if (exerciseError) {
        console.error('Error adding run details:', exerciseError);
        await deleteWorkout(workout.id).catch(() => undefined);
        toast.error(t('running.summary.toast.saveDetailsFailed'));
        setIsSavingRun(false);
        return;
      }

      saveSucceeded = true;
      console.log('Workout saved to history:', workout.id);
      toast.success(t('running.summary.toast.saveSuccess'));
    } catch (error) {
      console.error('Error saving workout to history:', error);
      toast.error(t('running.summary.toast.saveGenericError'));
      setIsSavingRun(false);
      return;
    }

    setIsSavingRun(false);
    if (!saveSucceeded) return;

    setShowRunSummary(false);
    Analytics.runningAnalysisPromptShown();
    setShowAnalyzePrompt(true);
  };
  
  // Handle delete run - show confirmation first
  const handleDeleteRun = () => {
    setShowDeleteConfirm(true);
  };
  
  // Confirm delete run
  const confirmDeleteRun = () => {
    setShowDeleteConfirm(false);
    setShowRunSummary(false);
    toast.success(t('running.summary.toast.runDeleted'));
    
    // Log run deleted
    Analytics.runningSummaryDeleted();
    navigate('/workouts');
  };
  
  // Handle analyze with Arnold
  const handleAnalyzeWithArnold = () => {
    setShowAnalyzePrompt(false);
    
    // Log analysis requested
    Analytics.runningAnalysisRequested(
      workoutSummary?.distance || 0,
      elapsedSeconds
    );
    
    // Build splits string for Arnold
    const splitsText = kmSplits.length > 0 
      ? kmSplits.map(s => `${s.km}km: ${s.pace}/km`).join(', ')
      : 'No splits recorded';
    
    // Add all data to the summary for Arnold
    const summaryWithAllData = {
      ...workoutSummary,
      title:
        runTitle.trim() ||
        defaultTitleWithKm(Number(workoutSummary?.distance) || 0),
      notes: runNotes.trim() || '',
      feeling: feelingOptions.find(f => f.id === feelingEmoji)?.label || 'Not specified',
      feelingId: feelingEmoji,
      splits: splitsText,
      kmSplits: kmSplits,
      userNotes: runNotes.trim()
    };
    
    // Store the workout summary for Arnold to analyze
    try {
      localStorage.setItem('pendingWorkoutAnalysis', JSON.stringify(summaryWithAllData));
      console.log('Saved running analysis to localStorage:', summaryWithAllData);
    } catch (e) {
      console.warn('Failed to save workout analysis to localStorage:', e);
    }
    
    // Navigate to chat with Arnold
    navigate('/chat');
  };
  
  // Handle skip analysis
  const handleSkipAnalysis = () => {
    setShowAnalyzePrompt(false);
    
    // Log analysis skipped
    Analytics.runningAnalysisSkipped();
    
    toast.success(t('running.summary.toast.skipAnalysis'));
    navigate('/workouts');
  };
  
  // Calculate current pace
  const distanceKm = totalDistance / 1000;
  const currentPaceSeconds = elapsedSeconds > 0 && distanceKm > 0 
    ? elapsedSeconds / distanceKm 
    : 0;
  
  // Calculate real-time calories using MET formula
  const userWeightKg = profile?.weight_kg || 70;
  const avgSpeedKmh = elapsedSeconds > 0 ? distanceKm / (elapsedSeconds / 3600) : 0;
  let currentMet = 6.0;
  if (gpsKind === 'walk') {
    if (avgSpeedKmh >= 6.5) currentMet = 5.0;
    else if (avgSpeedKmh >= 5) currentMet = 3.8;
    else currentMet = 3.3;
  } else if (gpsKind === 'hike') {
    if (avgSpeedKmh >= 5.5) currentMet = 9.0;
    else if (avgSpeedKmh >= 4) currentMet = 7.0;
    else currentMet = 6.0;
  } else {
    if (avgSpeedKmh >= 14) currentMet = 13.5;
    else if (avgSpeedKmh >= 12) currentMet = 11.5;
    else if (avgSpeedKmh >= 10) currentMet = 9.8;
    else if (avgSpeedKmh >= 8) currentMet = 8.3;
  }
  const currentCalories = Math.round(currentMet * userWeightKg * (elapsedSeconds / 3600));
  
  // Route data for display - memoized so map markers don't flicker when runTitle/runNotes change
  const routeSegmentsForMap = useMemo<[number, number][][]>(() =>
    routeSegments
      .filter(seg => seg.length >= 2)
      .map(seg => seg.map(p => [p.lat, p.lng] as [number, number])),
    [routeSegments]
  );
  const routeLine = useMemo<[number, number][]>(() =>
    routePoints.map(p => [p.lat, p.lng]),
    [routePoints]
  );
  // Stable center for summary map - prevents PlatformMap re-render when typing
  const mapCenterForSummary = useMemo<[number, number] | null>(() =>
    routeLine.length > 0 ? routeLine[Math.floor(routeLine.length / 2)] : null,
    [routeLine]
  );
  
  // Auto-follow: Update map center when user position changes (if auto-follow is on)
  useEffect(() => {
    if (currentPosition && isMapCentered) {
      setMapCenter([...currentPosition]); // Create new array to trigger re-render
    }
  }, [currentPosition, isMapCentered]);
  
  // Handle recenter map - go back to user's position and enable auto-follow
  const handleRecenterMap = () => {
    if (currentPosition) {
      setMapCenter([...currentPosition]); // Create new array to trigger re-render
    }
    setIsMapCentered(true); // Enable auto-follow, hide recenter button
    Analytics.runningMapRecentered();
  };
  
  // Handle map interaction (user scrolled/panned away) - disable auto-follow
  const handleMapInteraction = useCallback(() => {
    setIsMapCentered(false); // Disable auto-follow, show recenter button
    Analytics.runningMapScrolled();
  }, []);
  
  return (
    <div 
      className="fixed inset-0 bg-background flex flex-col"
      style={showRunSummary ? { pointerEvents: 'none' } : undefined}
    >
      {/* Map Background */}
      <div className="absolute inset-0 z-0">
        {!currentPosition ? (
          <div className="h-full w-full flex items-center justify-center bg-background">
            <div className="text-center">
              <MapPin className="w-12 h-12 text-primary mx-auto animate-pulse" />
              <div className="text-lg text-primary mt-4">{t('running.active.gpsAcquiring')}</div>
              <div className="text-sm text-muted-foreground mt-2">{t('running.active.pleaseWait')}</div>
            </div>
          </div>
        ) : (
          <PlatformMap 
            center={mapCenter || currentPosition}
            userLocation={currentPosition}
            routeSegments={routeSegmentsForMap}
            showUserMarker={true}
            showRoute={true}
            className="w-full h-full"
            darkMode={true}
            autoFollow={isMapCentered}
            onUserScroll={handleMapInteraction}
          />
        )}
        
        {/* Gradient overlays for readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/70 pointer-events-none" />
      </div>
      
      {/* Top Stats Bar */}
      <div 
        className="relative z-10 px-4"
        style={{ paddingTop: `calc(${SAFE_AREA.top} + 70px)` }}
      >
        <div className="bg-background/90 backdrop-blur-md rounded-2xl p-4 shadow-lg border border-border/30">
          {/* Timer - Large */}
          <div className="text-center mb-4">
            <div className="text-5xl font-mono font-bold text-foreground">
              {formatTime(elapsedSeconds)}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              {isPaused ? t('running.active.statusPaused') : t('running.active.statusRunning')}
            </div>
          </div>
          
          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-4">
            {/* Distance */}
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">
                {distanceKm.toFixed(2)}
              </div>
              <div className="text-xs text-muted-foreground uppercase">km</div>
            </div>
            
            {/* Pace */}
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground">
                {formatPace(currentPaceSeconds)}
              </div>
              <div className="text-xs text-muted-foreground uppercase">/km</div>
            </div>
            
            {/* Calories (estimated using MET formula) */}
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground">
                {currentCalories}
              </div>
              <div className="text-xs text-muted-foreground uppercase">kcal</div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Spacer to push controls to bottom */}
      <div className="flex-1" />
      
      {/* Bottom Controls */}
      <div 
        className="relative z-10 px-4 pb-4"
        style={{ paddingBottom: `calc(${SAFE_AREA.bottom} + 100px)` }}
      >
        {/* Quick Actions - Lock and GPS Signal aligned at bottom */}
        <div className="flex justify-between items-end mb-4">
          {/* Lock Button - aligned with GPS signal */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              const newLockedState = !isLocked;
              setIsLocked(newLockedState);
              if (newLockedState) {
                Analytics.runningScreenLocked();
              } else {
                Analytics.runningScreenUnlocked();
              }
            }}
            className="h-10 w-10 rounded-full bg-background/90 backdrop-blur-md shadow-lg border border-border/30"
          >
            {isLocked ? (
              <Lock className="h-4 w-4 text-destructive" />
            ) : (
              <Unlock className="h-4 w-4" />
            )}
          </Button>
          
          {/* GPS Signal + Re-center button stacked vertically */}
          <div className="flex flex-col items-center gap-2">
            {/* Re-center button - only visible when user has scrolled away */}
            {currentPosition && !isMapCentered && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRecenterMap}
                className="h-10 w-10 rounded-full bg-primary/90 backdrop-blur-md shadow-lg border border-border/30 animate-in fade-in-0 zoom-in-95 duration-200"
              >
                <Navigation className="h-4 w-4 text-primary-foreground" />
              </Button>
            )}
            
            {/* GPS Signal Strength */}
            <div className="h-10 px-3 rounded-full bg-background/90 backdrop-blur-md shadow-lg border border-border/30 flex items-center">
              <div className="flex items-end gap-0.5 h-4">
                {[1, 2, 3, 4].map((bar) => {
                  const bars = gpsSignal === 'strong' ? 4 : gpsSignal === 'medium' ? 3 : gpsSignal === 'weak' ? 2 : 1;
                  return (
                    <div
                      key={bar}
                      className={`w-1 rounded-sm transition-all ${
                        bar <= bars ? 'bg-primary' : 'bg-muted-foreground/30'
                      }`}
                      style={{ height: `${bar * 3 + 2}px` }}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        
        {/* Main Controls */}
        <div className="flex items-center justify-center gap-6">
          {/* Stop Button */}
          <Button
            onClick={handleStopClick}
            disabled={isLocked}
            variant="outline"
            className={`h-16 w-16 rounded-full border-2 ${
              isLocked ? 'opacity-50' : 'border-destructive text-destructive hover:bg-destructive/10'
            }`}
          >
            <Square className="h-6 w-6" />
          </Button>
          
          {/* Pause/Resume Button */}
          <Button
            onClick={isPaused ? handleResume : handlePause}
            disabled={isLocked}
            className={`h-20 w-20 rounded-full shadow-glow ${
              isLocked ? 'opacity-50 bg-muted' : 'bg-gradient-primary'
            }`}
          >
            {isPaused ? (
              <Play className="h-8 w-8 text-black ml-1" />
            ) : (
              <Pause className="h-8 w-8 text-black" />
            )}
          </Button>
          
          {/* Announce Stats Button */}
          <Button
            onClick={() => {
              lastManualAnnounceTimeRef.current = Date.now();
              Analytics.runningStatsAnnouncementRequested();
              Analytics.runningAudioAnnouncementPlayed('stats');
              announceStats();
            }}
            disabled={isLocked || !audioEnabled}
            variant="outline"
            className={`h-16 w-16 rounded-full border-2 ${
              isLocked || !audioEnabled ? 'opacity-50' : 'border-primary text-primary hover:bg-primary/10'
            }`}
          >
            <Volume2 className="h-6 w-6" />
          </Button>
        </div>
        
        {/* Lock indicator */}
        {isLocked && (
          <div className="text-center mt-4 text-sm text-muted-foreground">
            {t('running.active.screenLockedHint')}
          </div>
        )}
      </div>
      
      {/* Stop Confirmation Dialog - rendered in portal so it blocks ALL interaction including BottomNav */}
      {showStopConfirm && createPortal(
        <div 
          className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/80 p-4"
          style={{ top: 0, left: 0, right: 0, bottom: 0 }}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
        >
          <div 
            className="bg-background border border-border rounded-xl p-6 max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">{t('running.active.stopDialog.title')}</h2>
              <p className="text-sm text-muted-foreground">
                {t('running.active.stopDialog.description')}
              </p>
              <div className="flex flex-col gap-2 pt-2">
                <Button variant="outline" onClick={handleCancelStop} className="w-full h-11">
                  {t('running.active.stopDialog.continue')}
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleConfirmStop}
                  className="w-full h-11"
                >
                  {t('running.active.stopDialog.confirm')}
                </Button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Loading overlay - shown while saving workout after Run Summary */}
      {isSavingRun && createPortal(
        <div 
          className="fixed inset-0 z-[99998] flex flex-col items-center justify-center bg-background"
          style={{ 
            paddingTop: SAFE_AREA.top, 
            paddingBottom: SAFE_AREA.bottom, 
            paddingLeft: SAFE_AREA.left, 
            paddingRight: SAFE_AREA.right,
          }}
        >
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-base font-medium text-foreground">{t('running.summary.savingTitle')}</p>
          <p className="text-sm text-muted-foreground mt-1">{t('running.summary.savingSubtitle')}</p>
        </div>,
        document.body
      )}

      {/* STEP 1: Run Summary - Compact single-page layout, no scroll needed */}
      {showRunSummary && createPortal(
        <div 
          className="fixed inset-0 z-[99999] flex flex-col bg-background overflow-hidden ios-modal-scroll"
          style={{ 
            top: 0, left: 0, right: 0, bottom: 0,
            paddingTop: `calc(8px + ${SAFE_AREA.top})`, 
            paddingBottom: `calc(16px + ${SAFE_AREA.bottom})`, 
            paddingLeft: SAFE_AREA.left, 
            paddingRight: SAFE_AREA.right,
            boxSizing: 'border-box',
            touchAction: 'manipulation',
            pointerEvents: 'auto',
          }}
        >
          {/* Delete - fixed top-right, outside scroll flow, guaranteed to receive touches */}
          <button
            type="button"
            onClick={handleDeleteRun}
            className="fixed min-w-[48px] min-h-[48px] flex items-center justify-center text-destructive hover:text-destructive/80 touch-manipulation z-[100001]"
            style={{ 
              top: `calc(8px + ${SAFE_AREA.top})`, 
              right: `calc(8px + ${SAFE_AREA.right})` 
            }}
          >
            <Trash2 className="h-6 w-6" />
          </button>
          <div className="text-center shrink-0 pb-1">
            <div className="flex items-center justify-center">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center">
                <Footprints className="h-5 w-5 text-primary-foreground" />
              </div>
            </div>
            <h2 className="text-base font-bold text-center text-foreground">
              {t('running.summary.title')}
            </h2>
          </div>
          
          <div className="flex-1 min-h-0 flex flex-col px-4">
            {/* Map thumbnail - tap overlay captures all touches, expand icon bottom-right */}
            {routeLine.length > 1 && (
              <div 
                className="shrink-0 mb-2 rounded-lg overflow-hidden border border-border/30 h-28 relative cursor-pointer"
                style={{ minHeight: 112 }}
              >
                <PlatformMap
                  key="summary-map"
                  center={mapCenterForSummary}
                  routeSegments={routeSegmentsForMap}
                  showUserMarker={false}
                  showRoute={true}
                  showStartFinishMarkers={true}
                  showKmMarkers={true}
                  fitToRoute={true}
                  fitPaddingBottom={1.8}
                  autoFollow={false}
                  className="w-full h-full pointer-events-none"
                  darkMode={true}
                  interactive={false}
                  zoom={12}
                />
                {/* Full-map tap overlay - captures touches so expand works on iOS */}
                <div
                  className="absolute inset-0 z-10 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                  onClick={() => setShowExpandedMap(true)}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    setShowExpandedMap(true);
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={t('running.summary.expandMapAria')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setShowExpandedMap(true);
                    }
                  }}
                />
                {/* Expand icon - bottom right, visual only */}
                <div
                  className="absolute bottom-2 right-2 z-20 flex items-center justify-center w-10 h-10 rounded-lg bg-black/70 pointer-events-none"
                  aria-hidden
                >
                  <Maximize2 className="h-5 w-5 text-white" />
                </div>
              </div>
            )}
            <div 
              className="flex-1 min-h-0 overflow-y-auto space-y-2 py-1 ios-modal-scroll"
              style={{ 
                touchAction: 'pan-y', 
                WebkitOverflowScrolling: 'touch',
                paddingBottom: keyboardHeight > 0 ? keyboardHeight + 48 : undefined,
              } as React.CSSProperties}
            >
            {/* Editable Run Title */}
            <div>
              <label className="text-xs text-muted-foreground mb-0.5 block flex items-center gap-1">
                {t('running.summary.workoutNameLabel')} <Pencil className="h-3 w-3" />
              </label>
              <input
                type="text"
                value={runTitle}
                onChange={(e) => setRunTitle(e.target.value)}
                onBlur={() => {
                  Analytics.runningSummaryTitleEdited();
                }}
                className="w-full text-center font-semibold px-2 py-1.5 rounded-md border border-border bg-background/50 focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                placeholder={t('running.summary.runTitlePlaceholder')}
              />
            </div>
            
            {/* Stats Grid */}
            {workoutSummary && (
              <div className="grid grid-cols-2 gap-2 text-sm bg-muted/30 rounded-lg p-3 border border-border/30">
                <div className="text-center">
                  <div className="font-bold text-base text-primary">{workoutSummary.distance} km</div>
                  <div className="text-[10px] text-muted-foreground">{t('running.summary.statDistance')}</div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-base">{formatTime(elapsedSeconds)}</div>
                  <div className="text-[10px] text-muted-foreground">{t('running.summary.statTotalTime')}</div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-base">{workoutSummary.pace}</div>
                  <div className="text-[10px] text-muted-foreground">{t('running.summary.statAvgPacePerKm')}</div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-base">{workoutSummary.estimatedCalories}</div>
                  <div className="text-[10px] text-muted-foreground">{t('running.summary.statCalories')}</div>
                </div>
              </div>
            )}
            
            {/* Km Splits */}
            {kmSplits.length > 0 && (
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">{t('running.summary.splitsSection')}</label>
                <div className="space-y-0.5 max-h-24 overflow-y-auto bg-muted/20 rounded-md p-1.5">
                  {kmSplits.map((split, idx) => (
                    <div key={idx} className="flex justify-between items-center text-xs px-2 py-1 rounded-md hover:bg-muted/20">
                      <span className="text-muted-foreground font-medium">
                        {split.km >= 1 ? `${split.km} km` : `${split.km} km`}
                      </span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground">{split.time}</span>
                        <span className="font-mono font-semibold text-primary">{split.pace}/km</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* How did this run feel? */}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">{t('running.summary.feelingPrompt')}</label>
              <div className="flex justify-between gap-1.5 touch-manipulation">
                {feelingOptions.map((option) => {
                  const IconComponent = option.icon;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => {
                        setFeelingEmoji(option.id);
                        Analytics.runningSummaryFeelingSelected(option.id, option.label);
                      }}
                      className={`flex-1 py-2 rounded-md transition-all flex items-center justify-center ${
                        feelingEmoji === option.id 
                          ? 'bg-primary/20 ring-2 ring-primary scale-110' 
                          : 'bg-muted/30 hover:bg-muted/50'
                      }`}
                      title={option.label}
                    >
                      <IconComponent className={`h-5 w-5 ${feelingEmoji === option.id ? option.color : 'text-muted-foreground'}`} />
                    </button>
                  );
                })}
              </div>
              {feelingEmoji && (
                <p className="text-[10px] text-center text-muted-foreground mt-0.5">
                  {feelingOptions.find(f => f.id === feelingEmoji)?.label}
                </p>
              )}
            </div>
            
            {/* Notes - scroll into view when focused, extra padding when keyboard open */}
            <div className="pb-safe">
              <label className="text-xs text-muted-foreground mb-0.5 block">{t('running.summary.notesLabel')}</label>
              <textarea
                value={runNotes}
                onChange={(e) => setRunNotes(e.target.value)}
                onFocus={(e) => {
                  const target = e.target;
                  setTimeout(() => {
                    if (document.activeElement === target) {
                      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }, Capacitor.getPlatform() === 'ios' ? 500 : 400);
                }}
                onBlur={() => {
                  if (runNotes.trim().length > 0) {
                    Analytics.runningSummaryNotesAdded(runNotes.trim().length);
                  }
                }}
                placeholder={t('running.summary.notesPlaceholder')}
                style={{ scrollMarginBottom: keyboardHeight > 0 ? keyboardHeight + 80 : 100 }}
                className="w-full min-h-[80px] px-2 py-1.5 text-xs rounded-md border border-border bg-background/50 resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
            
            </div>
            {/* Share & Save Buttons - fixed at bottom, always visible */}
            <div className="flex gap-2 shrink-0 pt-2 touch-manipulation">
              <Button 
                onClick={() => {
                  const distanceKm = workoutSummary?.distance ?? 0;
                  const paceSeconds = distanceKm > 0 ? elapsedSeconds / distanceKm : 0;
                  setWorkoutToShare({
                    id: `run-${Date.now()}`,
                    title:
                      runTitle ||
                      defaultTitleWithKm(Number(workoutSummary?.distance) || 0),
                    duration_minutes: elapsedSeconds / 60,
                    completed_at: new Date().toISOString(),
                    workout_type: gpsWorkoutType.toLowerCase(),
                    distance_km: distanceKm,
                    average_pace: workoutSummary?.pace ?? '--:--',
                    calories_burned: workoutSummary?.estimatedCalories,
                    route_coordinates: routePoints.map(p => ({ lat: p.lat, lng: p.lng })),
                    duration_seconds: elapsedSeconds,
                    pace_seconds_per_km: paceSeconds,
                    splits: kmSplits,
                  } as any);
                  setShowShareModal(true);
                }}
                variant="outline"
                className="flex-1 h-10 touch-manipulation"
              >
                <ShareIcon className="h-4 w-4 mr-1.5 text-primary" />
                {t('running.summary.shareButton')}
              </Button>
            <Button 
              onClick={handleSaveRunSummary}
              className="flex-1 h-10 touch-manipulation bg-gradient-to-r from-primary to-accent text-black font-semibold"
            >
                {t('running.summary.saveButton')}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Share Modal for Run Summary */}
      <ShareWorkoutModal
        isOpen={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          setWorkoutToShare(null);
        }}
        workout={workoutToShare}
      />
      
      {/* STEP 2: Analyze with Arnold Dialog - dark overlay, no X, only two options */}
      <Dialog open={showAnalyzePrompt} onOpenChange={setShowAnalyzePrompt}>
        <DialogContent 
          hideClose 
          overlayClassName="bg-black"
          className="sm:max-w-sm max-w-[calc(100vw-2rem)] bg-background border-2 border-primary/30 shadow-2xl p-4 sm:p-6"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader className="text-center space-y-3 pb-2">
            <div className="mx-auto w-16 h-16 bg-gradient-primary rounded-full flex items-center justify-center shadow-glow">
              <Trophy className="h-8 w-8 text-gold" />
            </div>
            <DialogTitle className="text-xl font-bold text-center text-foreground">
              {t('running.summary.analysisPrompt.title')}
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              {runTitle}
            </p>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            <p className="text-center text-sm font-medium text-foreground">
              {t('running.summary.analysisPrompt.body')}
            </p>
            
            <div className="flex flex-col gap-2">
              <Button 
                onClick={handleAnalyzeWithArnold}
                className="w-full gap-2 h-11 font-semibold bg-gradient-primary text-primary-foreground hover:opacity-90"
              >
                <MessageCircle className="h-4 w-4" />
                {t('running.summary.analysisPrompt.analyzeWithCoach', { coach: profile?.ai_coach_name || 'Arnold' })}
              </Button>
              
              <Button 
                variant="outline" 
                onClick={handleSkipAnalysis}
                className="w-full h-11 font-semibold border-2"
              >
                {t('common.maybeLater')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Battery optimization prompt - Android only, for reliable background tracking */}
      {showBatteryPrompt && createPortal(
        <div 
          className="fixed inset-0 z-[99998] flex items-center justify-center bg-black/70 p-4"
          style={{ top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <div 
            className="bg-background border border-border rounded-xl p-5 max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-2">{t('running.active.batteryPrompt.title')}</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('running.active.batteryPrompt.body')}
            </p>
            <div className="flex flex-col gap-2">
              <Button
                onClick={async () => {
                  setShowBatteryPrompt(false);
                  try {
                    const { BatteryOptimization } = await import('@capawesome-team/capacitor-android-battery-optimization');
                    await BatteryOptimization.openBatteryOptimizationSettings();
                  } catch (_) {}
                }}
                className="w-full"
              >
                {t('common.openSettings')}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowBatteryPrompt(false);
                  localStorage.setItem('straiv_battery_prompt_dismissed', 'true');
                }}
                className="w-full"
              >
                {t('common.maybeLater')}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Dialog - must appear above Run Summary (z-99999) */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent 
          className="bg-background border-border z-[100000]"
          overlayClassName="z-[100000]"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>{t('running.summary.deleteDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('running.summary.deleteDialog.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="touch-manipulation min-h-[44px]">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteRun}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 touch-manipulation min-h-[44px]"
            >
              {t('running.summary.deleteDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Expanded Map - full-screen overlay, MUST be above Run Summary (z-99999) */}
      {showExpandedMap && createPortal(
        <div 
          className="fixed inset-0 z-[100002] flex flex-col bg-background"
          style={{ 
            top: 0, left: 0, right: 0, bottom: 0,
            paddingTop: SAFE_AREA.top,
            paddingBottom: SAFE_AREA.bottom,
            paddingLeft: SAFE_AREA.left,
            paddingRight: SAFE_AREA.right,
          }}
        >
          {/* Header with close */}
          <div className="shrink-0 flex items-center justify-between py-3 px-2">
            <div className="bg-background/90 backdrop-blur-sm rounded-lg px-3 py-2 border border-border">
              <div className="text-sm font-semibold">{runTitle || t('running.summary.expandedMapFallbackTitle')}</div>
              <div className="text-xs text-muted-foreground">
                {workoutSummary?.distance || 0} km • {formatTime(elapsedSeconds)}
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setShowExpandedMap(false)}
              className="min-w-[44px] min-h-[44px] shrink-0 touch-manipulation"
              aria-label={t('running.summary.closeMapAria')}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          {/* Map - full height, scrollable/zoomable */}
          <div className="flex-1 min-h-0 relative">
            {routeLine.length > 1 && (
              <PlatformMap
                key="expanded-map"
                center={routeLine.length > 0 ? routeLine[Math.floor(routeLine.length / 2)] : null}
                routeSegments={routeSegmentsForMap}
                showUserMarker={false}
                showRoute={true}
                showStartFinishMarkers={true}
                showKmMarkers={true}
                fitToRoute={true}
                className="w-full h-full"
                darkMode={true}
                interactive={true}
                zoom={14}
              />
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

